import type { BookingConfirmationConditions, BookingConfirmationSummary } from '@massilia/queries'

import { type FlightDurationSegmentLike, resolveFlightDurationMinutes } from '@massilia/contracts'
import { parseIsoLocal } from '@massilia/features'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { Briefcase, Luggage } from 'lucide-react'

import { travelClassTypesLabels } from '@/lib/schemas/flight-research-bar/flight-research-bar-base.schema'
import { formatDuration } from '@/utils/format-duration'
import { derivePassengerType } from '@/utils/passenger-type'

import {
  FlightSegmentCard,
  PassengerCard,
  TicketConditions,
} from '../booking/shared-trip-components'

type Segment = BookingConfirmationSummary['flightSegments'][number]

interface TripRecapSectionProps {
  summary?: BookingConfirmationSummary
}

export function TripRecapSection({ summary }: TripRecapSectionProps) {
  if (!summary || summary.flightSegments.length === 0) return null

  // Un groupe de vols par voyage (PNR), chacun avec SES bagages : les franchises du 1er
  // PNR ne valent pas pour le 2e. Un seul voyage = un seul groupe, sans en-tête.
  const tripGroups = groupSegmentsByTrip(summary.flightSegments)

  // MOD-INFANT : le frais bébé (10 %) n'est annoncé que si le dossier compte un bébé
  const infantCount = summary.passengers.filter(
    (passenger) => passenger.birthDate && derivePassengerType(passenger.birthDate) === 'Infant'
  ).length

  const conditionBlocks = resolveConditionBlocks(summary)

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-gray-light-200 bg-white p-4 md:p-6">
      <div className="text-lg leading-7 font-semibold text-gray-light-900 sm:text-2xl">
        Récapitulatif de votre voyage
      </div>

      {/* Conditions du billet (en haut) : un bloc par PNR dès qu'il y en a deux, chaque
          billet ayant les siennes. */}
      {conditionBlocks.map((block) => (
        <div
          className="flex flex-col gap-4 rounded-xl border border-gray-light-200 p-3 md:p-4"
          key={block.pnr ?? 'dossier'}
        >
          <div className="text-sm leading-4 font-semibold text-gray-light-900">
            Conditions du billet{block.pnr ? ` — ${block.pnr}` : ''}
          </div>
          <TicketConditions conditions={block.conditions} infantCount={infantCount} />
        </div>
      ))}

      {/* Vols, par voyage */}
      {tripGroups.map((group) => (
        <div className="flex flex-col gap-3" key={group.pnr ?? 'dossier'}>
          {group.pnr && (
            <div className="text-sm leading-4 font-semibold text-gray-light-900">
              Voyage {group.pnr}
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {group.segments.map((segment, i) => {
              const departureDateLabel = formatFlightDate(segment.departureAt)
              const arrivalDateLabel = formatFlightDate(segment.arrivalAt)
              const durationLabel = formatSegmentDuration(segment)
              const classLabel = segment.travelClass
                ? (travelClassTypesLabels[
                    segment.travelClass as keyof typeof travelClassTypesLabels
                  ] ?? segment.travelClass)
                : 'Économique'

              return (
                <FlightSegmentCard
                  key={i}
                  airline={segment.airline}
                  route={`${segment.departureLocation} vers ${segment.arrivalLocation}`}
                  departureLabel={departureDateLabel}
                  arrivalLabel={arrivalDateLabel}
                  stopsLabel="Direct"
                  durationLabel={durationLabel}
                  travelClass={classLabel}
                />
              )
            })}
          </div>
          <BaggageBlock pnr={group.pnr} segment={group.segments[0]} />
        </div>
      ))}

      {/* Passengers */}
      {summary.passengers.length > 0 && (
        <div className="flex flex-col gap-4 rounded-xl border border-gray-light-200 bg-white px-4 py-6">
          <div className="text-sm leading-4 font-semibold text-gray-light-900">
            Détails des voyageurs
          </div>
          {summary.passengers.map((passenger, i) => (
            <PassengerCard
              key={i}
              firstName={passenger.firstName}
              lastName={passenger.lastName}
              canceled={passenger.canceled}
              gender={passenger.gender}
              birthDate={passenger.birthDate}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/** Franchises bagages d'un voyage, lues sur son premier tronçon (identiques sur un PNR). */
function BaggageBlock({ pnr, segment }: { pnr: null | string; segment: Segment }) {
  const { cabinLuggageCount, cabinLuggageWeight, checkedLuggageCount, checkedLuggageWeight } =
    segment
  if (checkedLuggageCount <= 0 && cabinLuggageCount <= 0) return null
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-light-200 p-3 md:p-4">
      <div className="text-sm leading-4 font-semibold text-gray-light-900">
        Bagages inclus{pnr ? ` — ${pnr}` : ''}
      </div>
      {checkedLuggageCount > 0 && (
        <div className="flex items-start gap-5 pl-4">
          <Luggage className="size-6 shrink-0 text-gray-light-700" />
          <div className="flex flex-col gap-2">
            <div className="text-sm leading-4 font-medium text-gray-light-700">
              {checkedLuggageCount} bagage{checkedLuggageCount > 1 ? 's' : ''} soute
            </div>
            <div className="text-xs leading-4 font-medium text-gray-light-600">
              Poids maximum : {checkedLuggageWeight} kg chacun
            </div>
          </div>
        </div>
      )}
      {cabinLuggageCount > 0 && (
        <div className="flex items-start gap-5 pl-4">
          <Briefcase className="size-6 shrink-0 text-gray-light-700" />
          <div className="flex flex-col gap-2">
            <div className="text-sm leading-4 font-medium text-gray-light-700">
              {cabinLuggageCount} bagage{cabinLuggageCount > 1 ? 's' : ''} cabine
            </div>
            <div className="text-xs leading-4 font-medium text-gray-light-600">
              Poids maximum : {cabinLuggageWeight} kg chacun
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function formatFlightDate(isoDate: string): string {
  const date = parseIsoLocal(isoDate)
  if (!date) return isoDate
  const formatted = format(date, "EEE d MMM 'à' H'h'mm", { locale: fr })
  return formatted.charAt(0).toUpperCase() + formatted.slice(1)
}

function formatSegmentDuration(segment: FlightDurationSegmentLike): string {
  const totalMinutes = resolveFlightDurationMinutes(segment)
  if (totalMinutes === null) return '–'

  return formatDuration(totalMinutes)
}

// ── Helpers ──

/** Les tronçons par voyage, dans l'ordre des départs ; `pnr` nul quand il n'y a qu'un voyage. */
function groupSegmentsByTrip(segments: Segment[]): { pnr: null | string; segments: Segment[] }[] {
  const sorted = [...segments].sort((a, b) => a.departureAt.localeCompare(b.departureAt))
  const byPnr = new Map<string, Segment[]>()
  for (const segment of sorted) {
    const key = segment.pnr ?? ''
    byPnr.set(key, [...(byPnr.get(key) ?? []), segment])
  }
  if (byPnr.size <= 1) return [{ pnr: null, segments: sorted }]
  return [...byPnr.entries()].map(([pnr, group]) => ({ pnr: pnr || null, segments: group }))
}

/** Un bloc par PNR dès qu'il y en a deux, sinon le bloc unique du dossier, s'il existe. */
function resolveConditionBlocks(
  summary: BookingConfirmationSummary
): { conditions: BookingConfirmationConditions; pnr: null | string }[] {
  if (summary.tripConditions && summary.tripConditions.length > 1) return summary.tripConditions
  return summary.conditions ? [{ conditions: summary.conditions, pnr: null }] : []
}
